import { useEffect, useState } from "react";
import { api, apiError, imgUrl } from "../lib/api";
import { StudioShell } from "../components/StudioShell";
import { UploadZone } from "../components/UploadZone";
import { StyleSelect } from "../components/StyleSelect";
import { MaterialPicker } from "../components/MaterialPicker";
import { toast } from "sonner";
import { Clock, FolderPlus } from "lucide-react";

const CATEGORIES = ["apparel", "accessories", "shoes"];

export default function SketchStudio() {
  const [mode, setMode] = useState("sketch"); // sketch | prompt
  const [sketch, setSketch] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("apparel");
  const [styleId, setStyleId] = useState("");
  const [materialId, setMaterialId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState("");

  useEffect(() => {
    api.get("/designs").then((r) => setHistory(r.data.slice(0, 20))).catch(() => {});
    api.get("/collections").then((r) => setCollections(r.data)).catch(() => {});
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/generate/sketch", {
        sketch_b64: mode === "sketch" ? sketch : null, prompt, title, category,
        style_id: styleId || null, material_id: materialId || null,
      });
      setResult(data);
      setHistory((prev) => [data, ...prev.filter((d) => d.id !== data.id)]);
      toast.success("Render created");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const saveToCollection = async () => {
    if (!selectedCollection || !result) return;
    try {
      const { data } = await api.post(`/collections/${selectedCollection}/designs`, {
        design_id: result.id,
        version_id: result.versions?.[result.versions.length - 1]?.id || null,
      });
      if (data.duplicate) toast.info("Already in collection");
      else toast.success("Saved to collection");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const canGenerate = mode === "sketch" ? !!sketch : !!prompt.trim();

  return (
    <StudioShell title="Sketch to Render" subtitle="Turn a sketch — or just a description — into a photorealistic product render"
      onGenerate={generate} generating={generating} disabled={!canGenerate} result={result} generateLabel="Generate render">
      <div>
        <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Start from</label>
        <div className="mt-2 flex gap-2">
          <button data-testid="sketch-mode-sketch" onClick={() => setMode("sketch")}
            className={`px-4 py-2 rounded-md text-sm border transition-colors ${mode === "sketch" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:bg-zinc-100"}`}>Sketch upload</button>
          <button data-testid="sketch-mode-prompt" onClick={() => setMode("prompt")}
            className={`px-4 py-2 rounded-md text-sm border transition-colors ${mode === "prompt" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:bg-zinc-100"}`}>Prompt only</button>
        </div>
      </div>
      {mode === "sketch" && (
        <div>
          <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Design sketch</label>
          <div className="mt-2"><UploadZone value={sketch} onChange={setSketch} label="Upload your sketch" testId="sketch-upload-zone" /></div>
        </div>
      )}
      <div>
        <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Title</label>
        <input data-testid="sketch-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="SS26 Trench Coat"
          className="mt-2 w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
      </div>
      <div>
        <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Category</label>
        <div className="mt-2 flex gap-2">
          {CATEGORIES.map((c) => (
            <button key={c} data-testid={`sketch-category-${c}`} onClick={() => setCategory(c)}
              className={`px-4 py-2 rounded-md text-sm border capitalize transition-colors ${category === c ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:bg-zinc-100"}`}>
              {c}
            </button>
          ))}
        </div>
      </div>
      <MaterialPicker value={materialId} onChange={setMaterialId} testIdPrefix="sketch-material" />
      <StyleSelect value={styleId} onChange={setStyleId} />
      <div>
        <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{mode === "prompt" ? "Describe your design" : "Render notes (optional)"}</label>
        <textarea data-testid="sketch-prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
          placeholder={mode === "prompt" ? "e.g. an oversized double-breasted trench coat in beige gabardine with horn buttons and a belted waist" : "e.g. beige gabardine fabric, horn buttons, relaxed fit"}
          className="mt-2 w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
      </div>
      {/* Save to collection */}
      {result && (
        <div className="border-t border-zinc-200 pt-4 mt-4">
          <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-1"><FolderPlus size={12} /> Save to collection</label>
          <div className="mt-2 flex gap-2">
            <select value={selectedCollection} onChange={(e) => setSelectedCollection(e.target.value)}
              className="flex-1 border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
              <option value="">Choose collection...</option>
              {collections.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button onClick={saveToCollection} disabled={!selectedCollection}
              className="px-4 py-2 bg-zinc-950 text-white rounded-md text-sm font-medium hover:bg-zinc-800 disabled:opacity-40">Save</button>
          </div>
        </div>
      )}
      {/* Iteration history sidebar */}
      {history.length > 0 && (
        <div className="border-t border-zinc-200 pt-4 mt-4">
          <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-1"><Clock size={12} /> Recent iterations</label>
          <div className="mt-2 flex gap-2 overflow-x-auto pb-2">
            {history.map((d) => (
              <button key={d.id} onClick={() => setResult(d)}
                className={`shrink-0 border rounded-md overflow-hidden w-20 transition-colors ${result?.id === d.id ? "border-zinc-950 ring-2 ring-zinc-950" : "border-zinc-200 hover:border-zinc-400"}`}>
                <img src={imgUrl(d.cover_image)} alt={d.title} className="w-full h-16 object-cover" />
                <p className="text-[9px] p-1 text-center truncate text-zinc-500">{d.title}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </StudioShell>
  );
}
